/**
 * `navEntries` — the shell's navigation items, shared by every navigation
 * surface: the mobile bottom nav (`PrimaryNav`), the mobile menu sheet
 * (`MenuSheet`) and the desktop sidebar (`DesktopSidebar`).
 *
 * The sidebar has no list of its own. It regroups `NAV_ENTRIES` and
 * `SERVICE_ENTRIES` through `sidebarGroups`, so an entry added to either source
 * appears on desktop and mobile alike. The greying rules live here for the same
 * reason: the three surfaces read them through one hook and cannot disagree
 * about which items a member may open.
 */

import type { ComponentType, SVGProps } from 'react';

import type { MessageKeys } from '../i18n/messages/index.js';
import { type Capability, isCapabilityAllowed } from '../membership/capabilities.js';
import { useMembershipStatus } from '../membership/useMembershipStatus.js';
import { useSession } from '../session/useSession.js';

import { HomeIcon, MessagesIcon, ProfileIcon } from './icons.js';
import type { MenuShortcut, MenuShortcutChild } from './menuServiceEntries.js';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export interface NavEntry {
  readonly path: string;
  readonly labelKey: MessageKeys;
  readonly Icon: IconComponent;
  /**
   * Membership capability this entry maps to (028-membership-state-gating).
   * When set and not allowed for the active member's state, the entry is greyed
   * + `aria-disabled` rather than navigable. Koti (Home) has none (always
   * reachable). For a valid member every entry stays active (042/D-202 binary
   * access); for a `none` (non-active) member the capability entries grey out.
   */
  readonly capability?: Capability;
}

export const NAV_ENTRIES: readonly NavEntry[] = [
  { path: '/', labelKey: 'shell.primaryNav.koti', Icon: HomeIcon },
  {
    path: '/messages',
    labelKey: 'shell.primaryNav.viestit',
    Icon: MessagesIcon,
    capability: 'open_messages',
  },
  {
    path: '/profile',
    labelKey: 'shell.primaryNav.omatTiedot',
    Icon: ProfileIcon,
    capability: 'view_latest_invoice',
  },
] as const;

/** One sidebar row: a bottom-nav entry or a menu-sheet entry, never a third shape. */
export type NavItem =
  | { readonly source: 'nav'; readonly entry: NavEntry }
  | { readonly source: 'service'; readonly entry: MenuShortcut };

export interface SidebarGroups {
  /** Koti, then the menu sheet's destinations and actions in their own order. */
  readonly main: readonly NavItem[];
  /** The bottom nav's remaining entries: Viestit, Omat tiedot. */
  readonly footer: readonly NavItem[];
  /** The menu sheet's off-app links (the legal pages). */
  readonly secondary: readonly NavItem[];
}

const HOME_PATH = '/';

export function sidebarGroups(
  navEntries: readonly NavEntry[],
  serviceEntries: readonly MenuShortcut[],
): SidebarGroups {
  const nav = (entry: NavEntry): NavItem => ({ source: 'nav', entry });
  const service = (entry: MenuShortcut): NavItem => ({ source: 'service', entry });
  return {
    main: [
      ...navEntries.filter((entry) => entry.path === HOME_PATH).map(nav),
      ...serviceEntries.filter((entry) => entry.kind !== 'external-link').map(service),
    ],
    footer: navEntries.filter((entry) => entry.path !== HOME_PATH).map(nav),
    secondary: serviceEntries.filter((entry) => entry.kind === 'external-link').map(service),
  };
}

function matchesPath(pathname: string, to: string): boolean {
  if (to === HOME_PATH) return pathname === HOME_PATH;
  return pathname === to || pathname.startsWith(`${to}/`);
}

/** Whether a sub-menu child is the current page. */
export function matchesChild(pathname: string, child: MenuShortcutChild): boolean {
  return matchesPath(pathname, child.to);
}

/**
 * Whether `item` is the current page. A parent with a sub-menu is current when
 * one of its children is, so the parent stays marked while the sub-menu is
 * closed. Actions and off-app links are never the current page.
 */
export function matchesRoute(pathname: string, item: NavItem): boolean {
  if (item.source === 'nav') return matchesPath(pathname, item.entry.path);
  const { entry } = item;
  switch (entry.kind) {
    case 'flat-link':
      return matchesPath(pathname, entry.to);
    case 'expandable':
      return entry.children.some((child) => matchesChild(pathname, child));
    case 'action':
    case 'external-link':
      return false;
  }
}

/**
 * Per-item greying, for every navigation surface. Greying applies to a member
 * and to a briefly mounted anonymous visitor (the shell wraps the route guard,
 * so an anonymous visitor on a protected route mounts the chrome for one frame
 * before the redirect; an anonymous session classifies `none`, which greys its
 * capability entries instead of flashing them as links). An admin carries no
 * membership context and is never greyed.
 *
 * - Bottom-nav entries grey when their capability is not allowed.
 * - Menu-sheet destinations grey when `other_actions` is not allowed.
 * - "Ota yhteyttä" greys only for an anonymous session: the wizard follows its
 *   destination's own gating, and an anonymous visitor has no compose identity.
 * - Off-app links never grey.
 */
export function useNavItemGating(): (item: NavItem) => boolean {
  const { state } = useSession();
  const membershipStatus = useMembershipStatus();
  const isMembershipGated = state.kind === 'member' || state.kind === 'anonymous';
  const otherActionsAllowed = isCapabilityAllowed(membershipStatus, 'other_actions');

  return (item) => {
    if (item.source === 'nav') {
      const { capability } = item.entry;
      return (
        isMembershipGated &&
        capability !== undefined &&
        !isCapabilityAllowed(membershipStatus, capability)
      );
    }
    switch (item.entry.kind) {
      case 'action':
        return state.kind === 'anonymous';
      case 'external-link':
        return false;
      case 'flat-link':
      case 'expandable':
        return isMembershipGated && !otherActionsAllowed;
    }
  };
}
